import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { requestJsonData } from '~/utils/request-json-data';
import { getLoadingSkeletonFeed } from '~/services/layout/layout';
import type { Feed } from '~/services/layout';

type LoadingState = 'idle' | 'loading';

export type UseClientFeedOptions<TData> = {
  initialData?: TData | null;
  uiComponentId?: string;
};

type FeedState<TData> = {
  state: LoadingState;
  data: TData | null | undefined;
  stableData: TData | null | undefined;
};

/**
 * Custom hook that fetches client feed data using requestJsonData instead of Remix fetcher.
 * This prevents errors from bubbling up to the ErrorBoundary.
 */
export const useClientFeed = <TData = Feed>(
  clientFeedApiEndpoint: string | null,
  { initialData, uiComponentId }: UseClientFeedOptions<TData> = {}
) => {
  const [state, setState] = useState<LoadingState>('idle');
  const [data, setData] = useState(initialData);
  const previousDataRef = useRef<typeof initialData>(undefined);
  const isLoadingRef = useRef<boolean>(false);
  const currentFetchEndpointRef = useRef<string | null>(null);

  // Sync initialData into local state/ref when it changes; clear previousDataRef on endpoint change.
  useEffect(() => {
    if (initialData === undefined) {
      setData(undefined);
    } else {
      setData(initialData);
    }

    previousDataRef.current = undefined;
  }, [clientFeedApiEndpoint, initialData]);

  // uiComponentId change is expected to bring a new clientFeedApiEndpoint; reset loading state only.
  useEffect(() => {
    setState('idle');
    isLoadingRef.current = false;

    // Reset the fetch endpoint tracker
    currentFetchEndpointRef.current = null;
  }, [uiComponentId]);

  // Update stableData when data changes
  useEffect(() => {
    if (data) {
      previousDataRef.current = data;
    }
  }, [data]);

  const fetchData = useCallback(
    async (forceFresh: boolean = false) => {
      if (!clientFeedApiEndpoint || isLoadingRef.current) {
        return;
      }

      // Track the endpoint for this fetch to prevent stale responses
      const fetchEndpoint = clientFeedApiEndpoint;
      currentFetchEndpointRef.current = fetchEndpoint;
      isLoadingRef.current = true;
      setState('loading');

      let url = clientFeedApiEndpoint;
      if (forceFresh) {
        const urlObj = new URL(clientFeedApiEndpoint, window.location.origin);
        urlObj.searchParams.set('force-fresh', 'true');
        url = urlObj.pathname + urlObj.search;
      }

      const responseData = await requestJsonData({ url });

      // Only update state if this fetch is still for the current endpoint
      if (currentFetchEndpointRef.current !== fetchEndpoint) {
        return;
      }

      // requestJsonData returns undefined on error, so check if we got data
      if (responseData) {
        setData(responseData);
      }

      setState('idle');
      isLoadingRef.current = false;
    },
    [clientFeedApiEndpoint]
  );

  const loadingSkeletonFeed: Feed = getLoadingSkeletonFeed();

  const feedState: FeedState<TData> = useMemo(
    () => ({
      state,
      data: state === 'loading' ? (loadingSkeletonFeed as TData) : data,
      stableData: data || previousDataRef.current,
    }),
    [state, data]
  );

  return useMemo(() => ({ feedState, fetchData }), [feedState, fetchData]);
};
